import React from 'react';
import { BookmarkIcon, HomeIcon } from '@heroicons/react/24/outline';
import {
  BookmarkIcon as BookmarkIconSolid,
  HomeIcon as HomeIconSolid,
} from '@heroicons/react/24/solid';
import { useNotifications } from '../hooks/notifications/useNotifications';
import { useProfileImageHandling } from '../hooks/profile/useProfileImageHandling';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { useShellNavigation } from '../hooks/shell/useShellNavigation';
import { ErrorToast } from '../components/errorToast';
import { DesktopHeader } from './DesktopHeader';
import { MobileBottomNav } from './MobileBottomNav';
import { ShellFooter } from './ShellFooter';

interface ShellProps {
  children: React.ReactElement;
}

/**
 * Shell component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
const Shell = React.memo((props: ShellProps) => {
  console.log('render shell');

  // Business logic hooks (platform-agnostic)
  const { profile } = useAuthenticatedUser();
  const { unreadCount } = useNotifications();
  const { location, isActive, navigateToLogin } = useShellNavigation();
  const { profileImageUrl, handleImageError } = useProfileImageHandling(profile?.userId);

  // Static navigation configuration
  const navigationItems = [
    { name: 'My Trips', href: '/', icon: HomeIcon, iconSolid: HomeIconSolid },
    { name: 'Favorites', href: '/favorites', icon: BookmarkIcon, iconSolid: BookmarkIconSolid },
  ];

  return (
    <>
      <ErrorToast />
      <div className="flex flex-col min-h-screen">
        <div className="flex flex-col flex-1">
          {!location.pathname.startsWith('/login') && (
            <>
              <DesktopHeader
                profile={profile}
                navigationItems={navigationItems}
                isActive={isActive}
                navigateToLogin={navigateToLogin}
                profileImageUrl={profileImageUrl}
                onImageError={handleImageError}
              />

              {profile && (
                <MobileBottomNav
                  profile={profile}
                  navigationItems={navigationItems}
                  isActive={isActive}
                  unreadCount={unreadCount}
                  profileImageUrl={profileImageUrl}
                  onImageError={handleImageError}
                />
              )}
            </>
          )}
          <main className="flex-1 mb-16 md:mb-0">
            <div>
              <div className="w-full">{props.children}</div>
              <ShellFooter />
            </div>
          </main>
        </div>
      </div>
    </>
  );
});

export default Shell;
